import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";
import { toast } from "react-hot-toast";
import CreateProductPopup from "../CreateProductPopup/CreateProductPopup";
import BannerUploadPopup from "./BannerUploadPopup";
import BannerCarousel from "./BannerCarousel";
import {
	addSpotlightProduct,
	getSpotlightProducts,
	downvoteSpotlightProduct,
	upvoteSpotlightProduct,
	getSpotlightProductRequests,
	updateSpotlightProductRequestStatus,
	createEarlyGrowthProduct,
	getEarlyGrowthProducts,
} from "../../../Service/user";
import "./Products.scss";

// Imported Components
import RequestsModal from "./RequestsModal";
import ProductCard from "./ProductCard";
import PrizeShowcase from "./PrizeShowcase";
import MonthCarousel from "./MonthCarousel";
import MonthlyProducts from "./MonthlyProducts";

const getOrdinalSuffix = (day) => {
	if (day > 3 && day < 21) return 'th';
	switch (day % 10) {
		case 1: return 'st';
		case 2: return 'nd';
		case 3: return 'rd';
		default: return 'th';
	}
};

const Products = () => {
	const navigate = useNavigate();
	const [products, setProducts] = useState([]);
	const [industries, setIndustries] = useState([]);
	const [filteredProducts, setFilteredProducts] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [activeTab, setActiveTab] = useState("All");
	const [activeNav, setActiveNav] = useState("All");
	const [loading, setLoading] = useState(false);
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [nextDeadline, setNextDeadline] = useState("");
	const [topProducts, setTopProducts] = useState([]);
	const [monthCarousel, setMonthCarousel] = useState([]);
	const [activeMonth, setActiveMonth] = useState(0);
	const [monthlyProducts, setMonthlyProducts] = useState([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [isRequestsModalOpen, setIsRequestsModalOpen] = useState(false);
	const [productRequests, setProductRequests] = useState([]);
	const [loadingRequests, setLoadingRequests] = useState(false);
	const [sortBy, setSortBy] = useState("newest");
	const [stageTopProducts, setStageTopProducts] = useState({
		Idea: [],
		Early: [],
		Growth: [],
	});
	const [activeStageTab, setActiveStageTab] = useState("Idea");
	// New state for banner functionality
	const [isBannerUploadOpen, setIsBannerUploadOpen] = useState(false);
	const [banners, setBanners] = useState([]);
	const [loadingBanners, setLoadingBanners] = useState(false);

	const observer = useRef();
	const lastProductElementRef = useCallback(
		(node) => {
			if (loading || loadingMore) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasMore) {
					loadMoreProducts();
				}
			});
			if (node) observer.current.observe(node);
		},
		[loading, loadingMore, hasMore]
	);

	let loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));

	// Fetch product requests
	const fetchProductRequests = async () => {
		try {
			setLoadingRequests(true);
			const response = await getSpotlightProductRequests();
			if (response.status === 200) {
				setProductRequests(response.spotlightProductRequests);
			} else {
				toast.error(response.message || "Failed to fetch product requests");
			}
		} catch (error) {
			console.error("Error fetching product requests:", error);
			toast.error("An error occurred while fetching product requests");
		} finally {
			setLoadingRequests(false);
		}
	};

	// Update product request status
	const handleUpdateRequestStatus = async (requestId, status, remarkText) => {
		try {
			const response = await updateSpotlightProductRequestStatus(
				requestId,
				status,
				remarkText
			);
			if (response.status === 200) {
				toast.success(`Product request ${status} successfully`);
				// Refresh requests
				fetchProductRequests();
				// Also refresh products list if the status is approved
				if (status === "approved") {
					fetchProducts();
				}
			} else {
				toast.error(response.message || `Failed to ${status} product request`);
			}
		} catch (error) {
			console.error(`Error ${status} product request:`, error);
			toast.error(`An error occurred while ${status} the product request`);
		}
	};

	// Open requests modal
	const openRequestsModal = () => {
		fetchProductRequests();
		setIsRequestsModalOpen(true);
	};

	// Calculate next deadline (last day of current month or next month)
	useEffect(() => {
		const today = new Date();
		const currentMonth = today.getMonth();
		const year = today.getFullYear();

		const monthNames = [
			"January",
			"February",
			"March",
			"April",
			"May",
			"June",
			"July",
			"August",
			"September",
			"October",
			"November",
			"December",
		];

		// Get the last day of the current month
		const lastDay = new Date(year, currentMonth + 1, 0).getDate();
		setNextDeadline(`${lastDay}${getOrdinalSuffix(lastDay)} ${monthNames[currentMonth]} ${year}`);

		// Generate the months for carousel starting from June 2025
		const months = [];
		let carouselMonth = 5; // June (0-based index)
		let carouselYear = 2025;
		const currentDateObj = new Date();
		const currentYear = currentDateObj.getFullYear();
		const currentMonthIndex = currentDateObj.getMonth();

		// Calculate how many months to show based on current date
		let monthsToShow = 1; // Start with at least one month
		
		// If we're past May 2025, calculate how many months have passed
		if (currentYear > 2025 || (currentYear === 2025 && currentMonthIndex >= 4)) {
			// Only show months that have already started
			monthsToShow = Math.min(
				(currentYear - 2025) * 12 + (currentMonthIndex - 4) + 1,
				(currentYear - 2025) * 12 + (currentMonthIndex - 4)
			);
		}

		for (let i = 0; i < monthsToShow; i++) {
			months.push({
				month: carouselMonth,
				year: carouselYear,
				label: `${monthNames[carouselMonth]} '${String(carouselYear).slice(2)}`,
			});

			carouselMonth += 1;
			if (carouselMonth > 11) {
				carouselMonth = 0;
				carouselYear += 1;
			}
		}

		setMonthCarousel(months);
		setActiveMonth(months.length - 1); // Set the most recent month as active
	}, []);

	const fetchProducts = async (pageNum = 1, reset = true) => {
		try {
			if (reset) {
				setLoading(true);
			} else {
				setLoadingMore(true);
			}

			const response = await getSpotlightProducts(pageNum);
			if (response.status === 200) {
				if (reset) {
					setProducts(response.products);
					setFilteredProducts(response.products);
					setIndustries(response.industries);
				} else {
					// Prevent duplicate products by checking IDs
					const newProducts = response.products.filter(
						(newProduct) =>
							!products.some(
								(existingProduct) => existingProduct._id === newProduct._id
							)
					);

					setProducts((prev) => [...prev, ...newProducts]);
					setFilteredProducts((prev) => {
						const uniqueProducts = [...prev];
						newProducts.forEach((newProduct) => {
							if (!uniqueProducts.some((p) => p._id === newProduct._id)) {
								uniqueProducts.push(newProduct);
							}
						});
						return uniqueProducts;
					});
				}

				// Set hasMore to false if no new products or fewer than expected
				if (response.products.length === 0 || response.products.length < 10) {
					setHasMore(false);
				}

				// Get current month and year
				const today = new Date();
				const currentMonth = today.getMonth() + 1;
				const currentYear = today.getFullYear();

				// Filter current month's products
				const currentMonthProducts = response.products.filter((product) =>
					product.monthlySubmissions.some(
						(submission) =>
							submission.month === currentMonth &&
							submission.year === currentYear &&
							submission.status === "approved"
					)
				);

				// Calculate top products based on engagement for current month's products only
				const productsWithEngagement = currentMonthProducts.map((product) => {
					const totalEngagement =
						(product.upvotes ? product.upvotes.length : 0) +
						(product.comments ? product.comments.length : 0) +
						(product.shares || 0);
					return { ...product, totalEngagement };
				});

				// Sort by total engagement and take top 3
				const sorted = [...productsWithEngagement].sort(
					(a, b) => b.totalEngagement - a.totalEngagement
				);

				setTopProducts(sorted.slice(0, 3));

				// Filter products by the selected month
				if (monthCarousel.length > 0) {
					filterProductsByMonth(monthCarousel[activeMonth], response.products, activeStageTab);
				}
			} else {
				toast.error(response.message || "Failed to fetch products");
				setHasMore(false);
			}
		} catch (error) {
			console.error("Error fetching products:", error);
			toast.error("An error occurred while fetching products");
			setHasMore(false);
		} finally {
			setLoading(false);
			setLoadingMore(false);
		}
	};

	const loadMoreProducts = async () => {
		if (loadingMore || !hasMore) return;
		setLoadingMore(true);
		const nextPage = page + 1;
		setPage(nextPage);
		await fetchProducts(nextPage, false);
	};

	useEffect(() => {
		fetchProducts();
	}, []);

	// Filter products by month
	const filterProductsByMonth = (monthData, allProducts, activeStageTab) => {

		if (!allProducts || !monthData) {
			return;
		}
	
		// Create a new filtered array
		const filteredByMonth = allProducts.filter((product) => {
			const isStageMatch = product.stage === activeStageTab;
	
			const hasApprovedSubmissionInMonth = product.monthlySubmissions.some(
				(submission) =>
					submission.month === monthData.month + 1 && // month is 0-based
					submission.year === monthData.year &&
					submission.status === "approved"
			);
	
			return isStageMatch && hasApprovedSubmissionInMonth;
		});
	
		// Set the filtered products directly
		setMonthlyProducts(filteredByMonth);
	};

	// Handle month change in carousel
	useEffect(() => {

		if (monthCarousel.length > 0 && products.length > 0) {
			// Clear existing monthly products first
			setMonthlyProducts([]);
			// Then filter for the new month
			filterProductsByMonth(monthCarousel[activeMonth], products, activeStageTab);
		}
	}, [activeMonth, monthCarousel, products, activeStageTab]);

	// Apply filters and sorting
	useEffect(() => {
		// Make sure products is an array before filtering
		if (!Array.isArray(products)) return;

		let filtered = [...products];

		// Remove duplicates by ID
		filtered = filtered.filter(
			(product, index, self) =>
				index === self.findIndex((p) => p._id === product._id)
		);

		// Apply stage filter if not "All"
		if (activeNav !== "All") {
			filtered = filtered.filter(product => product.stage === activeNav);
		}

		// Apply category/industry filter if not "All"
		if (activeTab !== "All") {
			filtered = filtered.filter(
				(product) =>
					product.industry === activeTab ||
					product.tags.some((tag) => tag.toLowerCase().includes(activeTab))
			);
		}

		// Apply search filter
		if (searchTerm) {
			filtered = filtered.filter(
				(product) =>
					product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
					product.tagline.toLowerCase().includes(searchTerm.toLowerCase()) ||
					product.tags.some((tag) =>
						tag.toLowerCase().includes(searchTerm.toLowerCase())
					)
			);
		}

		// Apply sorting
		if (sortBy === "most-liked") {
			filtered.sort(
				(a, b) =>
					(b.upvotes ? b.upvotes.length : 0) - (a.upvotes ? a.upvotes.length : 0)
			);
		} else if (sortBy === "most-commented") {
			filtered.sort(
				(a, b) =>
					(b.comments ? b.comments.length : 0) -
					(a.comments ? a.comments.length : 0)
			);
		} else if (sortBy === "newest") {
			filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
		}

		setFilteredProducts(filtered);
	}, [activeTab, searchTerm, products, sortBy, activeNav]);

	// Add this useEffect to force check hasMore when filteredProducts changes
	useEffect(() => {
		if (filteredProducts.length === 0 && !loading && page > 1) {
			setHasMore(false);
		}
	}, [filteredProducts, loading, page]);

	const handleProductClick = (id) => {
		navigate(`/startup-spotlight/product/${id}`);
	};

	const handleCreateProduct = async (productData) => {
		try {
			setLoading(true);
			const response = await addSpotlightProduct(productData);

			if (response.status !== 200) {
				throw new Error(response.message || "Failed to create product");
			}

			toast.success(response.message);
			setIsPopupOpen(false);
			await fetchProducts();
		} catch (error) {
			const errorMessage =
				error.message || "Error creating product! Please try again.";
			toast.error(errorMessage);
			console.error("Error creating product:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleUpvoteProduct = async (e, productId) => {
		e.stopPropagation();
		try {
			const response = await upvoteSpotlightProduct(productId);
			if (response.status === 200) {
				toast.success(response.message || "Product upvoted successfully");
				// Update the products state with the updated product
				setProducts((prevProducts) =>
					prevProducts.map((product) =>
						product._id === productId ? response.product : product
					)
				);
				// Also update filteredProducts state
				setFilteredProducts((prevFiltered) =>
					prevFiltered.map((product) =>
						product._id === productId ? response.product : product
					)
				);

				// Recalculate top products
				fetchProducts();
			} else {
				toast.error(response.message || "Failed to upvote product");
			}
		} catch (error) {
			console.error("Error upvoting product:", error);
			toast.error("An error occurred while upvoting the product");
		}
	};

	const handleDownvoteProduct = async (e, productId) => {
		e.stopPropagation();
		try {
			const response = await downvoteSpotlightProduct(productId);
			if (response.status === 200) {
				toast.success(response.message || "Product downvoted successfully");
				fetchProducts(); // Refresh products to get updated counts
			} else {
				toast.error(response.message || "Failed to downvote product");
			}
		} catch (error) {
			console.error("Error downvoting product:", error);
			toast.error("An error occurred while downvoting the product");
		}
	};

	const handleMonthChange = (index) => {
		setActiveMonth(index);
	};

	// Render skeleton loaders when loading
	const renderSkeletonProducts = () => {
		return Array(4)
			.fill()
			.map((_, index) => (
				<div
					className="sp-product-card skeleton-card"
					key={`skeleton-${index}`}
				>
					<div className="product-header">
						<div className="product-logo">
							<SkeletonLoader type="avatar" width="80px" height="80px" />
						</div>
						<div className="product-description">
							<div className="product-title">
								<SkeletonLoader type="title" width="70%" />
								<SkeletonLoader type="text" width="30%" />
							</div>
							<SkeletonLoader type="text" count={2} />
							<div className="product-tags">
								<SkeletonLoader type="text" width="80%" height="24px" />
							</div>
						</div>
					</div>

					<div className="product-metrics">
						<div className="metric">
							<SkeletonLoader type="text" width="30px" height="20px" />
							<SkeletonLoader type="circle" width="20px" height="20px" />
						</div>
						<div className="metric">
							<SkeletonLoader type="text" width="30px" height="20px" />
							<SkeletonLoader type="circle" width="20px" height="20px" />
						</div>
					</div>
				</div>
			));
	};

	const handleSortChange = (newSortBy) => {
		// If clicking the same sort option again, do nothing
		if (newSortBy === sortBy) return;

		// Reset to page 1 when changing sort
		setPage(1);
		setSortBy(newSortBy);
		setHasMore(true);

		// Apply the sort directly here for immediate feedback
		let sorted = [...filteredProducts];

		if (newSortBy === "most-liked") {
			sorted.sort((a, b) => {
				const aupvotes = Array.isArray(a.upvotes) ? a.upvotes.length : 0;
				const bupvotes = Array.isArray(b.upvotes) ? b.upvotes.length : 0;
				return bupvotes - aupvotes;
			});
		} else if (newSortBy === "most-commented") {
			sorted.sort((a, b) => {
				const aComments = Array.isArray(a.comments) ? a.comments.length : 0;
				const bComments = Array.isArray(b.comments) ? b.comments.length : 0;
				return bComments - aComments;
			});
		} else if (newSortBy === "newest") {
			sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
		}

		setFilteredProducts(sorted);
	};

	// Fetch banners for the current nav section
	const fetchBanners = async () => {
		try {
			setLoadingBanners(true);
			// // Only pass stage if activeNav is Early or Growth
			// const stage = (activeStageTab === "Early" || activeStageTab === "Growth") ? activeStageTab : null;
			const response = await getEarlyGrowthProducts(activeStageTab);
			if (response.status === 200) {
				setBanners(response.spotlightProducts || []);
			} else {
				toast.error(response.message || "Failed to fetch banners");
			}
		} catch (error) {
			console.error("Error fetching banners:", error);
			toast.error("An error occurred while fetching banners");
		} finally {
			setLoadingBanners(false);
		}
	};

	// Handle banner upload
	const handleBannerUpload = async (bannerData) => {
		try {
			setLoading(true);
			const response = await createEarlyGrowthProduct(bannerData);
			
			if (response.status === 200) {
				toast.success("Banner uploaded successfully");
				fetchBanners(); // Refresh banners
			} else {
				toast.error(response.message || "Failed to upload banner");
			}
		} catch (error) {
			console.error("Error uploading banner:", error);
			toast.error("An error occurred while uploading the banner");
		} finally {
			setLoading(false);
		}
	};

	// Fetch banners when activeNav changes
	useEffect(() => {
		// if (activeStageTab === "Early" || activeStageTab === "Growth") {
			fetchBanners();
		// }
	}, [activeStageTab]);
	
	// Fetch banners for all stages on component mount
	useEffect(() => {
		fetchBanners();
	}, []);

	return (
		<div className="sp-products-container">
			{/* Showcase Section */}
			<div className="startup-showcase-container">
				<div className="showcase-header">
					<h1 className="showcase-title">
						Startups <span className="gradient-text">Showcase</span>
					</h1>
					<p className="showcase-subtitle">
						Submit your ideas by {nextDeadline}
					</p>
				</div>
				<div className="stage-tabs">
 					{Object.keys(stageTopProducts).map((stage) => (
 						<div
 							key={stage}
 							className={`stage-tab ${
 								activeStageTab === stage ? "active" : ""
 							}`}
 							onClick={() => setActiveStageTab(stage)}
 						>
 							{stage === "All" ? "All" : `${stage}`}
 							<span className="stage-tab-text">Stage</span>
 						</div>
 					))}
 				</div>
				
				{/* {activeStageTab === "Idea" || activeStageTab === "All" ? (
					<>
						<PrizeShowcase topProducts={topProducts} />
						<div className="next-top">
							<p>Next Top Projects Win Rs. 1,000 Each</p>
						</div>
					</>
				) : ( */}
					<div className="nav-section" style={{ marginTop: "20px", width: "100vw" }}>
						{loadingBanners ? (
							<div className="banner-loading" style={{ width: "100%", maxWidth: "1600px", margin: "0 auto" }}>
								<SkeletonLoader type="rectangle" height="450px" width="100%" style={{ borderRadius: "8px" }} />
							</div>
						) : banners.filter(banner => banner.stage === activeStageTab).length > 0 ? (
							<div className="cinematic-banner-container" style={{ width: "100%", maxWidth: "1600px", margin: "0 auto", position: "relative", overflow: "hidden", borderRadius: "8px", boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>
								<BannerCarousel banners={banners.filter(banner => banner.stage === activeStageTab)} />
								<div className="cinematic-overlay" style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "80px", background: "linear-gradient(transparent, rgba(0,0,0,0.7))", pointerEvents: "none" }}></div>
							</div>
						) : (
							<div className="banner-placeholder" style={{ width: "100%", maxWidth: "1600px", margin: "0 auto" }}>
								<SkeletonLoader type="rectangle" height="450px" width="100%" style={{ borderRadius: "8px", background: "linear-gradient(135deg, #1a1a1a, #333)" }}/>
								<div className="end-of-products" style={{ marginTop: "15px", textAlign: "center" }}>
									<span>No banners available for this stage</span>
								</div>
							</div>
						)}
					</div>
				{/* )} */}

				<div className="showcase-cta">
					<button
						className="post-idea-button"
						onClick={() => setIsPopupOpen(true)}
					>
						Post an Idea
					</button>
					<button className="view-requests-button" onClick={openRequestsModal}>
						View Requests
					</button>
				</div>

				{/* Month Carousel */}
				<MonthCarousel
					activeMonth={activeMonth}
					monthCarousel={monthCarousel}
					onMonthChange={handleMonthChange}
				/>

				{/* Display products for the selected month */}
				<MonthlyProducts
					loading={loading}
					activeStageTab={activeStageTab}
					monthlyLabel={monthCarousel[activeMonth]?.label || ""}
					stageProducts={monthlyProducts}
					handleUpvoteProduct={handleUpvoteProduct}
					handleDownvoteProduct={handleDownvoteProduct}
					handleProductClick={handleProductClick}
				/>
			</div>

			<div className="navigation">
				<div className="nav-items">
					{["All", "Idea", "Early", "Growth"].map((stage) => (
						<div
							key={stage}
							className={`nav-item ${activeNav === stage ? "active" : ""}`}
							onClick={() => setActiveNav(stage)}
						>
							{stage}
						</div>
					))}
				</div>
				<div className="navigation-buttons">
					{loggedInUser?.isAdmin && (
						<button className="add-banner-button" onClick={() => setIsBannerUploadOpen(true)}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
								<circle cx="8.5" cy="8.5" r="1.5"></circle>
								<polyline points="21 15 16 10 5 21"></polyline>
							</svg>
							Add Banner
						</button>
					)}
					<button className="view-requests-button" onClick={openRequestsModal}>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
							<rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
						</svg>
						View Requests
					</button>
					<button
						className="create-product-button"
						onClick={() => setIsPopupOpen(true)}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<line x1="12" y1="5" x2="12" y2="19"></line>
							<line x1="5" y1="12" x2="19" y2="12"></line>
						</svg>
						Post a Product or Idea
					</button>
				</div>
			</div>

			{/* Only show filters and products for Idea section or All */}
			
				<>
					<div className="filter-container">
						<div className="filter-tabs">
							<div
								className={`tab ${activeTab === "All" ? "active" : ""}`}
								onClick={() => setActiveTab("All")}
							>
								All
							</div>
							{industries.map((industry) => (
								<div
									className={`tab ${activeTab === industry ? "active" : ""}`}
									key={industry}
									onClick={() => setActiveTab(industry)}
								>
									{industry}
								</div>
							))}
						</div>

						<div className="search-container">
							<input
								type="text"
								placeholder="Search"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
							<svg
								className="search-icon"
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<circle cx="11" cy="11" r="8"></circle>
								<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
							</svg>
						</div>
					</div>

					{/* Sorting options */}
					<div className="filter-options">
						<span className="filter-label">Sort by:</span>
						<div className="filter-buttons">
							<button
								className={`filter-button ${sortBy === "newest" ? "active" : ""}`}
								onClick={() => handleSortChange("newest")}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
								</svg>
								Newest
							</button>
							<button
								className={`filter-button ${
									sortBy === "most-liked" ? "active" : ""
								}`}
								onClick={() => handleSortChange("most-liked")}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M7 10v12"></path>
									<path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path>
								</svg>
								Most Liked
							</button>
							<button
								className={`filter-button ${
									sortBy === "most-commented" ? "active" : ""
								}`}
								onClick={() => handleSortChange("most-commented")}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
								</svg>
								Most Comments
							</button>
						</div>
					</div>

					<h1 className="sp-products-title">All Products</h1>

					<div className="sp-products-grid">
						{loading && page === 1
							? renderSkeletonProducts()
							: filteredProducts.map((product, index) => {
									// Check if this is one of the top 3 products
									const topIndex = topProducts.findIndex(
										(p) => p._id === product._id
									);

									// Add a ref to the last element for infinite scrolling
									const isLastProduct = index === filteredProducts.length - 1;

									return (
										<ProductCard
											key={product._id}
											product={product}
											onClick={handleProductClick}
											onUpvote={handleUpvoteProduct}
											onDownvote={handleDownvoteProduct}
											isTopProduct={topIndex !== -1}
											topRank={topIndex}
											ref={isLastProduct ? lastProductElementRef : null}
										/>
									);
							  })}

						{/* Loading indicator for infinite scroll */}
						{loadingMore && (
							<div className="loading-indicator">
								<div className="loading-spinner"></div>
							</div>
						)}

						{/* End of products message */}
						{!loading &&
							!loadingMore &&
							filteredProducts.length > 0 &&
							!hasMore && (
								<div className="end-of-products">
									<span>End of products</span>
								</div>
							)}

						{/* Empty state when no products */}
						{!loading && filteredProducts.length === 0 && (
							<div className="no-products">
								<p>No products found matching your criteria.</p>
							</div>
						)}
					</div>
				</>
		

			<CreateProductPopup
				isOpen={isPopupOpen}
				onClose={() => setIsPopupOpen(false)}
				onSubmit={handleCreateProduct}
			/>

			{/* Product Requests Modal */}
			<RequestsModal
				isOpen={isRequestsModalOpen}
				onClose={() => setIsRequestsModalOpen(false)}
				requests={productRequests}
				onUpdateStatus={handleUpdateRequestStatus}
				isAdmin={loggedInUser?.isAdmin}
				loading={loadingRequests}
			/>

			{/* Banner Upload Popup */}
			<BannerUploadPopup
				isOpen={isBannerUploadOpen}
				onClose={() => setIsBannerUploadOpen(false)}
				onSubmit={handleBannerUpload}
			/>
		</div>
	);
};

export default Products;
